import { Alert, Button, Col, Collapse, Row, Tooltip, Typography } from 'antd'
import { SupportedJurisdictions, useCurrentUser } from 'api'
import { QuestionCircleOutlined } from '@ant-design/icons'
import Container from 'components/Container'
import FormikInput from 'components/FormikInput'
import dayjs from 'dayjs'
import { useFormikContext } from 'formik'
import { Checkbox, DatePicker, Form, FormItem, Input, Select } from 'formik-antd'
import { useLocalization } from 'localization'
import { useUserCurrency } from 'modules/common/hooks'
import { FC, useEffect, useMemo, useState } from 'react'
import { ITransactionFormValues, useDynamicTransactionFields, useTransactionModal } from './hooks'
import Styled from './styled'
import WalletFormDropdown from 'modules/integrations/WalletFormDropdown'
import { TransactionType } from 'api/transactions/dtos'
import TooltipIcon from 'components/TooltipIcon'

interface ITransactionForm extends ReturnType<typeof useTransactionModal> {}

const TransactionForm: FC<ITransactionForm> = ({
	transactionOptions,
	theme,
	t,
	isTransactionLoading,
	getTransactionProperties,
	innerCalculated,
	setInnerCalculated,
	isTransactionFetched,
}) => {
	const { locale } = useLocalization()
	const { data: user } = useCurrentUser()
	const { values, isValid, dirty, isSubmitting, setFieldValue, resetForm } = useFormikContext<ITransactionFormValues>()
	const { outgoing, incoming, wallet, blockchain, noFee, showAcquisitionDate, showAdditionalCosts, showCapitalLoss } =
		useDynamicTransactionFields(user?.jurisdiction)
	const currency = useUserCurrency()
	const transactionProperties = useMemo(
		() => (values.type ? getTransactionProperties(values.type) : []),
		[getTransactionProperties, values.type]
	)
	const [isInitialized, setIsInitialized] = useState(false)
	const [prevType, setPrevType] = useState(values.type)

	useEffect(() => {
		// to prevent natural form reset too early
		if (values.type && prevType) {
			resetForm({
				values: {
					type: values.type,
				},
			})
		}
		setPrevType(values.type)
	}, [values.type])

	useEffect(() => {
		if (isTransactionFetched || !values.id) {
			setTimeout(() => {
				setIsInitialized(true)
			}, 1000)
		}
	}, [isTransactionFetched])

	useEffect(() => {
		if (values.fee?.perCoin && values.fee?.amount) {
			const total = values.fee.amount * values.fee.perCoin
			setInnerCalculated(prev => ({
				...prev,
				fee: total,
			}))

			if (isInitialized || !values.fee.valuation) {
				setFieldValue('calculatedFeeValuation', total)
			}
		}
	}, [values.fee?.perCoin, values.fee?.amount])

	useEffect(() => {
		if (values.inPerCoin && values.inAmount) {
			const total = values.inAmount * values.inPerCoin
			setInnerCalculated(prev => ({
				...prev,
				in: total,
			}))

			if (isInitialized || !values.inValuation) {
				setFieldValue('calculatedInValuation', total)
			}
		}
	}, [values.inAmount, values.inPerCoin])

	useEffect(() => {
		if (values.outPerCoin && values.outAmount) {
			const total = values.outAmount * values.outPerCoin
			setInnerCalculated(prev => ({
				...prev,
				out: total,
			}))

			if (isInitialized || !values.outValuation) {
				setFieldValue('calculatedOutValuation', total)
			}
		}
	}, [values.outPerCoin, values.outAmount])

	const jurisdictionWarning = useMemo(() => {
		let suffix = ''

		if (values.type && user?.jurisdiction === SupportedJurisdictions.UK) {
			if (values.type === TransactionType.CRYPTO_LIQUIDITY_POOL_DEPOSIT) {
				suffix = values.inCurrency ? '.tokenProvided' : '.tokenNotProvided'
			}
			if (values.type === TransactionType.CRYPTO_LIQUIDITY_POOL_WITHDRAWAL) {
				suffix = values.outCurrency ? '.tokenProvided' : '.tokenNotProvided'
			}
		}

		const key = `transactions.jurisdictionWarnings.${user?.jurisdiction}.${values.type}${suffix}`
		const translation = t(key)
		return key === translation ? undefined : translation
	}, [t, user?.jurisdiction, values.type, values.inCurrency, values.outCurrency])

	return (
		<Form layout="vertical">
			<Container>
				<Row gutter={[theme.margins.md, theme.margins.sm]}>
					<Col xs={24} sm={12}>
						<FormItem name="type" label={t('terms.transactionType')} required>
							<Select showSearch name="type" placeholder={t('placeholders.transactionType')}>
								{transactionOptions.map(({ groupName, types }) => (
									<Select.OptGroup key={groupName} label={groupName}>
										{types.map(({ label, value }) => (
											<Select.Option key={value} value={value}>
												{label}
											</Select.Option>
										))}
									</Select.OptGroup>
								))}
							</Select>
						</FormItem>
					</Col>
					<Col xs={24} sm={12}>
						<FormItem name="date" label={t('terms.date')} required>
							<DatePicker
								name="date"
								{...(locale.includes('en')
									? {
											format: 'D/M/YYYY h:mm:ss A',
									  }
									: undefined)}
								placeholder={t('placeholders.date')}
								onBlur={e => {
									const newStringValue = e.target.value
									const dateValue = dayjs(newStringValue, locale.includes('en') ? 'D/M/YYYY h:mm:ss A' : undefined)
									setFieldValue('date', dateValue.toISOString())
								}}
								showTime
							/>
						</FormItem>
					</Col>
				</Row>
				{jurisdictionWarning && values.type !== TransactionType.CRYPTO_MINTED_SELL && (
					<Container margin={{ bottom: 'sm' }}>
						<Alert type="warning" message={jurisdictionWarning} />
					</Container>
				)}
				{showAcquisitionDate && (
					<Row gutter={[theme.margins.md, 0]}>
						<Col xs={24} sm={12}>
							<FormItem name="acquisitionDate" label={t('transactions.transactionModal.acquisitionDate')}>
								<DatePicker name="acquisitionDate" placeholder={t('placeholders.date')} showTime />
							</FormItem>
						</Col>
					</Row>
				)}
				{showCapitalLoss && (
					<Row gutter={[theme.margins.md, 0]}>
						<Col xs={24} sm={12}>
							<FormItem
								name="capitalLoss"
								label={t('transactions.transactionModal.capitalLoss')}
								required={!values.outAmount}
							>
								<FormikInput
									type="number"
									disabled={!!values.outAmount}
									name="capitalLoss"
									placeholder={t('placeholders.amount')}
									onChange={value => {
										if (value) {
											setFieldValue('outAmount', undefined)
										}
										setFieldValue('capitalLoss', value)
									}}
								/>
							</FormItem>
						</Col>
					</Row>
				)}
				{outgoing?.dynamicVisibility && (
					<Row gutter={[theme.margins.md, 0]}>
						<Col xs={24}>
							<Container margin={{ bottom: 'sm' }}>
								<Checkbox name="isMarginLossInValue">
									<Container display="flex" alignItems="center">
										<Typography>{t('transactions.transactionModal.marginLossCheckbox')}</Typography>
										<TooltipIcon
											title={t('transactions.transactionModal.marginLossCheckboxTooltip')}
											color={theme.colors.gray['500']}
										>
											<QuestionCircleOutlined />
										</TooltipIcon>
									</Container>
								</Checkbox>
							</Container>
						</Col>
					</Row>
				)}
				<Row gutter={[theme.margins.sm, 0]}>
					{values.type && outgoing && (
						<>
							{!outgoing.dynamicVisibility || !values.isMarginLossInValue ? (
								<>
									<Col xs={24}>
										<Styled.GroupLabel strong>{outgoing.title || t('terms.outgoing')}</Styled.GroupLabel>
									</Col>
									<Col xs={24} sm={12}>
										<FormItem name="outAmount" label={t('terms.amount')} required={!outgoing.amountNotRequired}>
											<FormikInput
												type="number"
												disabled={!!values.capitalLoss}
												name="outAmount"
												placeholder={t('placeholders.amount')}
												onChange={value => {
													if (value) {
														setFieldValue('capitalLoss', undefined)
													}
													setFieldValue('outAmount', value)
												}}
											/>
										</FormItem>
									</Col>

									<Col xs={24} sm={12}>
										<FormItem name="outCurrency" label={t('terms.currency')} required={!outgoing.currencyNotRequired}>
											<FormikInput name="outCurrency" placeholder={outgoing.placeholder} />
										</FormItem>
									</Col>
									{!outgoing.noValuation && (
										<>
											<Col xs={24} sm={12}>
												<Tooltip title={t('transactions.transactionModal.pricePerUnitTooltip')}>
													<FormItem name="outPerCoin" label={t('terms.perCoin')}>
														<FormikInput type="number" name="outPerCoin" placeholder={t('placeholders.pricePerCoin')} />
													</FormItem>
												</Tooltip>
											</Col>

											<Col xs={24} sm={12}>
												<Tooltip title={t('transactions.transactionModal.valuationTooltip')}>
													<FormItem
														name="calculatedOutValuation"
														label={t('terms.valuation')}
														extra={
															values.calculatedOutValuation !== undefined &&
															values.calculatedOutValuation !== null &&
															((innerCalculated.out !== undefined &&
																values.calculatedOutValuation !== innerCalculated.out) ||
																!innerCalculated.out) ? (
																<Typography.Text type="warning">
																	{t('transactions.transactionModal.valuationChangeNote')}
																</Typography.Text>
															) : undefined
														}
													>
														<FormikInput
															type="number"
															name="calculatedOutValuation"
															placeholder={t('placeholders.valuation')}
														/>
													</FormItem>
												</Tooltip>
											</Col>
										</>
									)}
								</>
							) : (
								<Col xs={24} sm={12}>
									<FormItem name="marginLossValue" label={t('transactions.transactionModal.marginLossValue')} required>
										<FormikInput type="number" name="marginLossValue" placeholder={t('placeholders.amount')} />
									</FormItem>
								</Col>
							)}
						</>
					)}
					{showAdditionalCosts && (
						<Col xs={24}>
							<FormItem
								name="additionalCosts"
								label={t('transactions.transactionModal.additionalCosts')}
								{...(currency
									? {
											extra: t('settings.taxes.currency', { currency }),
									  }
									: undefined)}
							>
								<FormikInput type="number" name="additionalCosts" placeholder={t('placeholders.amount')} />
							</FormItem>
						</Col>
					)}
					{showAdditionalCosts && jurisdictionWarning && values.type === TransactionType.CRYPTO_MINTED_SELL && (
						<Container margin={{ bottom: 'sm' }}>
							<Alert type="warning" message={jurisdictionWarning} />
						</Container>
					)}
					{incoming?.dynamicVisibility && (
						<Col xs={24}>
							<Container margin={{ bottom: 'sm' }}>
								<Checkbox name="isMarginProfitInValue">
									<Container display="flex" alignItems="center">
										<Typography>{t('transactions.transactionModal.marginProfitCheckbox')}</Typography>

										<TooltipIcon
											title={t('transactions.transactionModal.marginProfitCheckboxTooltip')}
											color={theme.colors.gray['500']}
										>
											<QuestionCircleOutlined />
										</TooltipIcon>
									</Container>
								</Checkbox>
							</Container>
						</Col>
					)}
					{values.type && incoming && (
						<>
							{!incoming.dynamicVisibility || !values.isMarginProfitInValue ? (
								<>
									<Col xs={24}>
										<Styled.GroupLabel strong>{t('terms.incoming')}</Styled.GroupLabel>
									</Col>
									<Col xs={24} sm={12}>
										<FormItem name="inAmount" label={t('terms.amount')} required={!incoming.amountNotRequired}>
											<FormikInput type="number" name="inAmount" placeholder={t('placeholders.amount')} />
										</FormItem>
									</Col>
									<Col xs={24} sm={12}>
										<FormItem name="inCurrency" label={t('terms.currency')} required={!incoming.currencyNotRequired}>
											<FormikInput name="inCurrency" placeholder={incoming.placeholder} />
										</FormItem>
									</Col>
									{!incoming.noValuation && (
										<>
											<Col xs={24} sm={12}>
												<Tooltip title={t('transactions.transactionModal.pricePerUnitTooltip')}>
													<FormItem name="inPerCoin" label={t('terms.perCoin')}>
														<FormikInput type="number" name="inPerCoin" placeholder={t('placeholders.pricePerCoin')} />
													</FormItem>
												</Tooltip>
											</Col>

											<Col xs={24} sm={12}>
												<Tooltip title={t('transactions.transactionModal.valuationTooltip')}>
													<FormItem
														name="calculatedInValuation"
														label={t('terms.valuation')}
														extra={
															values.calculatedInValuation !== undefined &&
															values.calculatedInValuation !== null &&
															((innerCalculated.in !== undefined &&
																values.calculatedInValuation !== innerCalculated.in) ||
																!innerCalculated.in) ? (
																<Typography.Text type="warning">
																	{t('transactions.transactionModal.valuationChangeNote')}
																</Typography.Text>
															) : undefined
														}
													>
														<FormikInput
															type="number"
															name="calculatedInValuation"
															placeholder={t('placeholders.valuation')}
														/>
													</FormItem>
												</Tooltip>
											</Col>
										</>
									)}
									{incoming.fork && (
										<Col xs={24} sm={12}>
											<FormItem
												name="forkBaseCurrency"
												label={t('transactions.transactionModal.forkedFromCurrency')}
												required
											>
												<FormikInput name="forkBaseCurrency" placeholder={t('placeholders.crypto')} />
											</FormItem>
										</Col>
									)}
								</>
							) : (
								<Col xs={24} sm={12}>
									<FormItem
										name="marginProfitValue"
										label={t('transactions.transactionModal.marginProfitValue')}
										required
									>
										<FormikInput type="number" name="marginProfitValue" placeholder={t('placeholders.amount')} />
									</FormItem>
								</Col>
							)}
						</>
					)}
					{values.type && !noFee && (
						<>
							<Col xs={24}>
								<Styled.GroupLabel strong>{t('terms.fees')}</Styled.GroupLabel>
							</Col>
							<Col xs={24} sm={12}>
								<FormItem name="fee.amount" label={t('terms.amount')}>
									<FormikInput type="number" name="fee.amount" placeholder={t('placeholders.amount')} />
								</FormItem>
							</Col>
							<Col xs={24} sm={12}>
								<FormItem name="fee.currency" label={t('terms.currency')}>
									<FormikInput name="fee.currency" placeholder={t('placeholders.cryptoOrFiat')} />
								</FormItem>
							</Col>
							<Col xs={24} sm={12}>
								<Tooltip title={t('transactions.transactionModal.pricePerUnitTooltip')}>
									<FormItem name="fee.perCoin" label={t('terms.perCoin')}>
										<FormikInput type="number" name="fee.perCoin" placeholder={t('placeholders.pricePerCoin')} />
									</FormItem>
								</Tooltip>
							</Col>
							<Col xs={24} sm={12}>
								<Tooltip title={t('transactions.transactionModal.valuationTooltip')}>
									<FormItem
										name="calculatedFeeValuation"
										label={t('terms.valuation')}
										extra={
											values.calculatedFeeValuation !== undefined &&
											values.calculatedFeeValuation !== null &&
											((innerCalculated.fee !== undefined && values.calculatedFeeValuation !== innerCalculated.fee) ||
												!innerCalculated.fee) ? (
												<Typography.Text type="warning">
													{t('transactions.transactionModal.valuationChangeNote')}
												</Typography.Text>
											) : undefined
										}
									>
										<FormikInput
											type="number"
											name="calculatedFeeValuation"
											placeholder={t('placeholders.valuation')}
										/>
									</FormItem>
								</Tooltip>
							</Col>
						</>
					)}
				</Row>
				{values.type && outgoing?.internalWallet && (
					<Container>
						<Row gutter={[theme.margins.md, 0]}>
							<Col xs={12}>
								<WalletFormDropdown
									name="out.wallet.id"
									label={t('transactions.transactionModal.fromWallet')}
									placeholder={t('placeholders.walletName')}
								/>
							</Col>
						</Row>
					</Container>
				)}
				{values.type && incoming?.internalWallet && (
					<Container>
						<Row gutter={[theme.margins.md, 0]}>
							<Col xs={12}>
								<WalletFormDropdown
									name="in.wallet.id"
									label={t('transactions.transactionModal.toWallet')}
									placeholder={t('placeholders.walletName')}
								/>
							</Col>
						</Row>
					</Container>
				)}
				{values.type && wallet && (
					<Container>
						<Row gutter={[theme.margins.md, 0]}>
							<Col xs={12}>
								<WalletFormDropdown
									name="wallet.id"
									label={t('terms.wallet')}
									placeholder={t('placeholders.walletName')}
								/>
							</Col>
						</Row>
					</Container>
				)}
				{values.type && blockchain && (
					<Container>
						<Row gutter={[theme.margins.md, 0]}>
							<Col xs={24}>
								<FormItem name="blockchainId" label={t('transactions.transactionModal.blockchainTransactionId')}>
									<FormikInput name="blockchainId" placeholder={t('placeholders.hashed')} />
								</FormItem>
							</Col>
							{blockchain.outInternalWallet ? (
								<Col xs={12}>
									<WalletFormDropdown
										name="out.wallet.id"
										label={t('transactions.transactionModal.fromWallet')}
										placeholder={t('placeholders.walletName')}
									/>
								</Col>
							) : (
								<>
									<Col xs={12}>
										<FormItem name="out.source" label={t('transactions.transactionModal.fromTransactionSource')}>
											<FormikInput name="out.source" placeholder={t('placeholders.transactionSource')} />
										</FormItem>
									</Col>
									<Col xs={12}>
										<FormItem name="out.wallet.address" label={t('transactions.transactionModal.fromWalletAddress')}>
											<FormikInput name="out.wallet.address" placeholder={t('placeholders.walletAddress')} />
										</FormItem>
									</Col>
								</>
							)}
							{blockchain.inInternalWallet ? (
								<Col xs={12}>
									<WalletFormDropdown
										name="in.wallet.id"
										label={t('transactions.transactionModal.toWallet')}
										placeholder={t('placeholders.walletName')}
									/>
								</Col>
							) : (
								<>
									<Col xs={12}>
										<FormItem name="in.source" label={t('transactions.transactionModal.toTransactionSource')}>
											<FormikInput name="in.source" placeholder={t('placeholders.transactionSource')} />
										</FormItem>
									</Col>
									<Col xs={12}>
										<FormItem name="in.wallet.address" label={t('transactions.transactionModal.toWalletAddress')}>
											<FormikInput name="in.wallet.address" placeholder={t('placeholders.walletAddress')} />
										</FormItem>
									</Col>
								</>
							)}
						</Row>
					</Container>
				)}
				<Row gutter={[theme.margins.md, 0]}>
					<Col xs={24} sm={12}>
						<FormItem name="group" label={t('transactions.transactionModal.group')}>
							<FormikInput name="group" placeholder={t('placeholders.group')} />
						</FormItem>
					</Col>
				</Row>
				<FormItem name="note" label={t('transactions.transactionModal.note')}>
					<Input.TextArea rows={3} name="note" placeholder={t('placeholders.note')} />
				</FormItem>
				<Collapse>
					{values.type && transactionProperties.length > 0 && (
						<Collapse.Panel header={t('terms.properties')} key="2">
							{transactionProperties.map(({ title, key, options }) => (
								<FormItem key={key} name={`properties.${key}`} label={title}>
									<Select name={`properties.${key}`} placeholder={t('placeholders.property')} options={options} />
								</FormItem>
							))}
						</Collapse.Panel>
					)}
				</Collapse>
				<Button
					type="primary"
					htmlType="submit"
					loading={isSubmitting}
					disabled={isTransactionLoading || !isValid || !dirty}
				>
					{t('actions.save')}
				</Button>
			</Container>
		</Form>
	)
}

export default TransactionForm
